import { z } from 'zod'
import { LEAVE_APPLIES_TO, LEAVE_COLOURS } from './types'

const ymd = (message: string) => z.string().regex(/^\d{4}-\d{2}-\d{2}$/, message)

export const leaveRequestSchema = z
  .object({
    typeId: z.string().min(1, 'Choose a leave type.').transform(Number),
    startDate: ymd('Choose the first day of leave.'),
    endDate: ymd('Choose the last day of leave.'),
    halfDay: z.boolean(),
    reason: z
      .string()
      .trim()
      .min(1, 'Give a reason for the leave.')
      .max(1000, 'Use at most 1000 characters.'),
    attachmentFileId: z.number().nullable(),
  })
  .refine((v) => v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'The last day must be on or after the first day.',
  })
  .refine((v) => !v.halfDay || v.startDate === v.endDate, {
    path: ['halfDay'],
    message: 'A half day can only be requested for a single day.',
  })

export type LeaveRequestFormInput = z.input<typeof leaveRequestSchema>
export type LeaveRequestFormOutput = z.output<typeof leaveRequestSchema>
export const LEAVE_REQUEST_FIELDS = [
  'typeId',
  'startDate',
  'endDate',
  'halfDay',
  'reason',
  'attachmentFileId',
  'staffId',
  'studentId',
  'kind',
] as const

export const leaveTypeSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name.').max(100, 'Use at most 100 characters.'),
  appliesTo: z.enum(LEAVE_APPLIES_TO),
  maxDaysPerYear: z
    .string()
    .transform((v) => (v.trim() === '' ? null : Number(v)))
    .refine(
      (v) => v === null || (Number.isInteger(v) && v >= 0 && v <= 365),
      'Enter a whole number between 0 and 365, or leave empty for unlimited.',
    ),
  requiresAttachment: z.boolean(),
  isPaid: z.boolean(),
  colour: z.enum(LEAVE_COLOURS),
  active: z.boolean(),
})

export type LeaveTypeFormInput = z.input<typeof leaveTypeSchema>
export type LeaveTypeFormOutput = z.output<typeof leaveTypeSchema>
export const LEAVE_TYPE_FIELDS = [
  'name',
  'appliesTo',
  'maxDaysPerYear',
  'requiresAttachment',
  'isPaid',
  'colour',
  'active',
] as const
